'use client';
import Link from 'next/link';
import { FEATURE_FLAGS } from '../../lib/config';

const CATEGORIES = [
    {
        title: 'Account & Profile',
        icon: '👤',
        questions: [
            { q: 'How do I create an account?', a: 'Click the "Sign Up" button on the home page and follow the prompts. You can sign up using email or social login.' },
            { q: 'Can I change my username?', a: 'Currently, usernames are permanent once set to ensure leaderboard integrity.' },
        ]
    },
    {
        title: 'Gameplay',
        icon: '🎮',
        questions: [
            { q: 'How do I join a game?', a: 'Find an open lobby on the dashboard and click "Join". Make sure you have enough balance for paid games.' },
            { q: 'What happens if I disconnect?', a: 'We try to keep you in the game for one round, but if you don\'t submit an answer, you will be eliminated.' },
        ]
    },
    {
        title: 'Financials',
        icon: '💰',
        questions: [
            { q: 'How do I withdraw my winnings?', a: 'Go to your Wallet section and click "Withdraw". Minimum withdrawal is £5.' },
            { q: 'Are my payments secure?', a: 'Yes, we use industry-standard encryption and trusted payment gateways to process all transactions.' },
        ]
    },
    {
        title: 'Technical Support',
        icon: '🛠️',
        questions: [
            { q: 'The game is lagging, what should I do?', a: 'Check your internet connection or try refreshing the page. We recommend a stable Wi-Fi connection.' },
            { q: 'I found a bug, how do I report it?', a: 'Please contact us at info@playobvio.app with details and screenshots if possible.' },
        ]
    }
];

export default function HelpCenterPage() {
    return (
        <div className="min-h-screen bg-[#F8F7F4]">
            <header className="sticky top-0 z-40 backdrop-blur-md bg-[#F8F7F4]/90 border-b border-gray-200/50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link href="/" className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                            <span className="text-indigo-600 font-black text-lg">O</span>
                        </div>
                        <span className="font-extrabold text-xl tracking-tight text-gray-900">OBVIO</span>
                    </Link>
                    <Link href="/" className="bg-gray-900 text-white px-4 py-2 rounded-full font-semibold hover:bg-gray-800 transition-colors text-sm">
                        ← Home
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 py-16">
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 tracking-tight">Help Center</h1>
                    <p className="text-gray-500 text-lg">Find answers to common questions and learn how to get the most out of Obvio.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                    {CATEGORIES.map((cat, idx) => (
                        <div key={idx} className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-4 mb-6">
                                <span className="text-4xl">{cat.icon}</span>
                                <h2 className="text-2xl font-bold text-gray-900">{cat.title}</h2>
                            </div>
                            <div className="space-y-6">
                                {cat.questions.map((q, qIdx) => (
                                    <div key={qIdx}>
                                        <h3 className="font-bold text-gray-900 mb-2">{q.q}</h3>
                                        <p className="text-gray-600 text-sm leading-relaxed">{q.a}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-16 bg-indigo-600 rounded-3xl p-10 text-center text-white">
                    <h2 className="text-3xl font-bold mb-4">Still need help?</h2>
                    <p className="text-indigo-100 mb-8 max-w-lg mx-auto">Our support team is available 24/7 to help you with any issues or questions you might have.</p>
                    <Link href="/contact" className="bg-white text-indigo-600 px-8 py-3 rounded-full font-bold hover:bg-indigo-50 transition-colors inline-block">
                        Contact Support
                    </Link>
                </div>
            </main>

            <footer className="py-12 border-t border-gray-200 text-center">
                <p className="text-gray-400 text-sm">© 2026 OBVIO. All rights reserved.</p>
            </footer>
        </div>
    );
}
